/**
 * LibraryTools — tool registry listing page.
 *
 * Two entry buttons at the top: "+ New server tool" and "+ New client
 * tool". Builtin tools are seeded, show up in the list as read-only.
 *
 * The table shows kind (server / client / builtin), name, description,
 * target (URL for server, handler.fn for builtin), and parameter count.
 * Clicking a row navigates to the full-page editor (not a modal).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

export default function LibraryTools() {
  const nav = useNavigate();
  const { isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();

  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("all");
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.listTools();
        setTools(res.data || []);
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const t of tools) {
      const c = t.category || "Other";
      if (!seen.has(c)) {
        seen.add(c);
        list.push(c);
      }
    }
    list.sort();
    return [
      { id: "all", label: "All categories" },
      ...list.map((c) => ({ id: c, label: c })),
    ];
  }, [tools]);

  const kinds = useMemo(() => {
    const seen = new Set();
    for (const t of tools) seen.add(t.kind || "unknown");
    return ["all", ...Array.from(seen).sort()];
  }, [tools]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tools.filter((t) => {
      if (category !== "all" && (t.category || "Other") !== category) return false;
      if (kind !== "all" && (t.kind || "unknown") !== kind) return false;
      if (q) {
        const hay = `${t.name || t._id} ${t.description || ""} ${t.category || ""} ${t.kind || ""} ${t.request?.url || ""} ${t.handler?.module || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tools, category, kind, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const c = t.category || "Other";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(t);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.name || a._id).localeCompare(b.name || b._id));
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const paramCount = (t) => {
    if (t.kind === "server") {
      return (
        (t.request?.path_params || []).length +
        (t.request?.query_params || []).length +
        (t.request?.body_params || []).length
      );
    }
    if (t.kind === "client" || t.kind === "builtin") {
      return (t.parameters || []).length;
    }
    return 0;
  };

  return (
    <>
      <PageHeader
        title="Tools"
        subtitle={`Registry of every tool available to agents. ${tools.length} tools across ${categories.length - 1} categories.`}
        breadcrumbs={[
          {
            label: "Library",
            to: isLive ? "/library/live/platform" : "/library/draft/platform",
          },
          { label: "Tools" },
        ]}
        actions={
          isLive ? null : (
            <div className="btn-row">
              <button
                className="btn"
                onClick={() => nav("/library/draft/tools/new?kind=server")}
                title="Create a new tool that calls a backend endpoint"
              >
                ⊷ New server tool
              </button>
              <button
                className="btn primary"
                onClick={() => nav("/library/draft/tools/new?kind=client")}
                title="Create a new tool that runs in the caller's client"
              >
                🔧 New client tool
              </button>
            </div>
          )
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <div
          className="mb-16"
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {categories.map((c) => (
              <button
                key={c.id}
                className={`btn sm ${category === c.id ? "primary" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
                <span className="muted" style={{ marginLeft: 4 }}>
                  (
                  {c.id === "all"
                    ? tools.length
                    : tools.filter((t) => (t.category || "Other") === c.id).length}
                  )
                </span>
              </button>
            ))}
          </div>
        </div>

        <div
          className="mb-16"
          style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}
        >
          <span className="muted small">Kind:</span>
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {kinds.map((k) => (
              <button
                key={k}
                className={`btn sm ${kind === k ? "primary" : ""}`}
                onClick={() => setKind(k)}
              >
                {k === "all" ? "all" : k}
                <span className="muted" style={{ marginLeft: 4 }}>
                  (
                  {k === "all"
                    ? tools.length
                    : tools.filter((t) => (t.kind || "unknown") === k).length}
                  )
                </span>
              </button>
            ))}
          </div>
          <div className="spacer" />
          <input
            type="text"
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              minWidth: 220,
            }}
          />
          <span className="muted small">
            {filtered.length} tool{filtered.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <h3>No tools</h3>
            <p>Try widening the filters or add a new tool from the top bar.</p>
          </div>
        ) : (
          grouped.map(([cat, list]) => (
            <div key={cat} className="mb-24">
              <h3 className="section-label">
                {cat}
                <span className="muted small" style={{ marginLeft: 8, fontWeight: 400 }}>
                  ({list.length})
                </span>
              </h3>
              <div className="card" style={{ padding: 0 }}>
                <table className="table tools-table" style={{ border: "none" }}>
                  <thead>
                    <tr>
                      <th style={{ width: 220 }}>Tool</th>
                      <th>Description</th>
                      <th style={{ width: 110 }}>Kind</th>
                      <th style={{ width: 260 }}>Target</th>
                      <th className="right" style={{ width: 100 }}>
                        Params
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((t) => (
                      <tr
                        key={t.tool_id || t._id}
                        className="clickable-row"
                        onClick={() =>
                          nav(`${prefix}/tools/${encodeURIComponent(t.tool_id || t._id)}`)
                        }
                      >
                        <td>
                          <div className="mono" style={{ fontWeight: 600 }}>
                            {t.name || t._id}
                          </div>
                          {t.system_tool && (
                            <span
                              className="badge badge-neutral"
                              style={{ marginTop: 4 }}
                            >
                              system
                            </span>
                          )}
                        </td>
                        <td className="small">
                          {t.description || (
                            <span className="subtle">no description</span>
                          )}
                        </td>
                        <td>
                          <KindBadge kind={t.kind} />
                        </td>
                        <td className="small">
                          <TargetCell tool={t} />
                        </td>
                        <td className="right">
                          {paramCount(t) > 0 ? (
                            <span className="mono small">{paramCount(t)}</span>
                          ) : (
                            <span className="subtle">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}

// ─── Kind badge ─────────────────────────────────────────────────────
export function KindBadge({ kind }) {
  const k = kind || "unknown";
  const COLORS = {
    server: "#1f5fb0",
    client: "#0f7a47",
    builtin: "#6f42c1",
    unknown: "#6b7280",
  };
  const color = COLORS[k] || COLORS.unknown;
  return (
    <span
      className="kind-badge"
      style={{ background: `${color}1a`, color, borderColor: `${color}44` }}
    >
      {k}
    </span>
  );
}

function TargetCell({ tool }) {
  if (tool.kind === "server" && tool.request) {
    return (
      <span className="mono small">
        <span style={{ color: "var(--color-text-muted)" }}>
          {tool.request.method || "GET"}
        </span>{" "}
        {shortenUrl(tool.request.url)}
      </span>
    );
  }
  if (tool.kind === "builtin" && tool.handler) {
    return (
      <span className="mono small">
        {tool.handler.module ? `${tool.handler.module}.` : ""}
        {tool.handler.function || "<unknown>"}
      </span>
    );
  }
  if (tool.kind === "client") {
    return <span className="muted small">runs on caller's client</span>;
  }
  return <span className="subtle">—</span>;
}

function shortenUrl(url) {
  if (!url) return "";
  return url.length > 44 ? url.slice(0, 42) + "…" : url;
}
